/**
 * Where the camera looks, from the device's orientation or from a drag (Sprint 103.0).
 *
 * The orientation follows the W3C DeviceOrientation convention: an earth frame
 * of East-North-Up, and the device's own frame with x to the right of the
 * screen, y to its top and z out of the screen towards the viewer. Its
 * rotation is Z-X'-Y'' (alpha, beta, gamma). The back camera looks along the
 * device's -z.
 */

import { cross, normalise, type CameraBasis, type Vector } from './projection.ts';

const RADIANS = Math.PI / 180;

export interface DeviceAngles {
  /** Degrees about z, counter-clockwise from north when the reading is absolute. */
  readonly alpha: number;
  readonly beta: number;
  readonly gamma: number;
}

/**
 * The camera basis for a device orientation and a screen orientation angle
 * (`screen.orientation.angle`: 0 portrait, 90 when the device is turned a
 * quarter counter-clockwise, 270 clockwise).
 */
export function basisFromDevice(angles: DeviceAngles, screenAngle: number): CameraBasis {
  const a = angles.alpha * RADIANS;
  const b = angles.beta * RADIANS;
  const g = angles.gamma * RADIANS;
  const cA = Math.cos(a);
  const sA = Math.sin(a);
  const cB = Math.cos(b);
  const sB = Math.sin(b);
  const cG = Math.cos(g);
  const sG = Math.sin(g);
  // The columns of R = Rz(alpha) * Rx(beta) * Ry(gamma): each device axis in the earth frame.
  const x: Vector = [cA * cG - sA * sB * sG, cG * sA + cA * sB * sG, -cB * sG];
  const y: Vector = [-cB * sA, cA * cB, sB];
  const z: Vector = [cG * sA * sB + cA * sG, sA * sG - cA * cG * sB, cB * cG];

  const t = screenAngle * RADIANS;
  const cT = Math.cos(t);
  const sT = Math.sin(t);
  // The screen's right and up, which turn with the screen inside the device.
  const right: Vector = [cT * x[0] - sT * y[0], cT * x[1] - sT * y[1], cT * x[2] - sT * y[2]];
  const up: Vector = [sT * x[0] + cT * y[0], sT * x[1] + cT * y[1], sT * x[2] + cT * y[2]];
  const forward: Vector = [-z[0], -z[1], -z[2]];
  return { right, up, forward };
}

/** The camera basis for a view turned to `yaw` (an azimuth) and tilted up by `pitch`. */
export function basisFromLook(yawDegrees: number, pitchDegrees: number): CameraBasis {
  const yaw = yawDegrees * RADIANS;
  const pitch = pitchDegrees * RADIANS;
  const forward: Vector = [
    Math.sin(yaw) * Math.cos(pitch),
    Math.cos(yaw) * Math.cos(pitch),
    Math.sin(pitch)
  ];
  const up: Vector = [
    -Math.sin(yaw) * Math.sin(pitch),
    -Math.cos(yaw) * Math.sin(pitch),
    Math.cos(pitch)
  ];
  return { right: cross(forward, up), up, forward };
}

/**
 * One step of smoothing towards a new reading. Sensor readings jitter by a
 * degree or two; the vectors are blended and re-orthonormalised rather than the
 * angles, which wrap at 360° and swap near the upright pose.
 */
export function smoothBasis(previous: CameraBasis, next: CameraBasis, amount: number): CameraBasis {
  const blend = (p: Vector, n: Vector): Vector => [
    p[0] + (n[0] - p[0]) * amount,
    p[1] + (n[1] - p[1]) * amount,
    p[2] + (n[2] - p[2]) * amount
  ];
  const forward = normalise(blend(previous.forward, next.forward));
  const rawUp = blend(previous.up, next.up);
  // Gram-Schmidt: up loses whatever of it lies along forward.
  const along = rawUp[0] * forward[0] + rawUp[1] * forward[1] + rawUp[2] * forward[2];
  const up = normalise([
    rawUp[0] - along * forward[0],
    rawUp[1] - along * forward[1],
    rawUp[2] - along * forward[2]
  ]);
  return { right: cross(forward, up), up, forward };
}

const angleBetween = (a: Vector, b: Vector): number =>
  Math.acos(Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]))) / RADIANS;

/**
 * Smooths the camera's pose with a one-euro filter (Sprint 103.1): the amount
 * follows how fast the phone turns, measured between consecutive readings and
 * itself smoothed, so a still phone is held steady and a turn is followed.
 */
export class PoseSmoother {
  private shown: CameraBasis | undefined;
  private previous: { basis: CameraBasis; at: number } | undefined;
  private speed = 0;
  private readonly amountFor: (dtSeconds: number, speedDegreesPerSecond: number) => number;

  constructor(amountFor: (dtSeconds: number, speedDegreesPerSecond: number) => number) {
    this.amountFor = amountFor;
  }

  next(reading: CameraBasis, nowMs: number): CameraBasis {
    const before = this.previous;
    this.previous = { basis: reading, at: nowMs };
    if (this.shown === undefined || before === undefined) {
      this.shown = reading;
      return reading;
    }
    const dt = Math.max(1e-3, (nowMs - before.at) / 1000);
    const turned =
      angleBetween(before.basis.forward, reading.forward) +
      angleBetween(before.basis.up, reading.up) / 2;
    // The speed itself, smoothed at 1 Hz, as the filter's paper does.
    const k = 1 / (1 + 1 / (2 * Math.PI) / dt);
    this.speed += (turned / dt - this.speed) * k;
    this.shown = smoothBasis(this.shown, reading, this.amountFor(dt, this.speed));
    return this.shown;
  }

  reset(): void {
    this.shown = undefined;
    this.previous = undefined;
    this.speed = 0;
  }
}
